import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DealHierarchy from "../shared/DealHierarchy";
import AddDealModal from "./AddDealModal";

function CompanyRevenueTab() {
  const { getVerifiedJWT } = useAuth();
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedSellingYearId, setSelectedSellingYearId] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDealOpened, setAddDealOpened] = useState(false);

  const fetchSellingYears = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/selling-year/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years = Array.isArray(result) ? result : result.data || [];
        setSellingYears(years);
        const activeYear = years.find(y => y.is_active);
        const defaultYear = activeYear || years[0];
        if (defaultYear) {
          setSelectedSellingYearId(defaultYear.id);
        }
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  const fetchRevenue = useCallback(async () => {
    if (!selectedSellingYearId) return;
    setLoading(true);
    setRevenueData([]);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(
          `/v1/ps/deal-hierarchy?selling_year_id=${selectedSellingYearId}`
        ),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = (result && result.data) || result || {};
        setRevenueData(Array.isArray(data) ? data : []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, selectedSellingYearId]);

  useEffect(() => {
    fetchSellingYears();
  }, [fetchSellingYears]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="xs" align="center">
          <Text size="sm" fw={500}>
            Selling Year
          </Text>
          <Select
            value={selectedSellingYearId ? String(selectedSellingYearId) : null}
            onChange={value => {
              if (!value) return;
              const match = sellingYears.find(y => String(y.id) === value);
              setSelectedSellingYearId(match ? match.id : null);
            }}
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            placeholder="Select selling year"
            allowDeselect={false}
            searchable={false}
            w={200}
          />
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setAddDealOpened(true)}
        >
          Add Deal
        </Button>
      </Group>

      <AddDealModal
        opened={addDealOpened}
        onClose={() => setAddDealOpened(false)}
        sellingYears={sellingYears}
        initialYearId={selectedSellingYearId}
        onCreated={() => {
          setAddDealOpened(false);
          fetchRevenue();
        }}
      />

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <DealHierarchy records={revenueData} />
      )}
    </Stack>
  );
}

export default CompanyRevenueTab;
